import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Icon } from '../../../components/icons/Icon'
import { InlineAlert } from '../../../components/InlineAlert'
import { StatusPill } from '../../../components/StatusPill'
import { lifecycleStatusPill } from '../cartridge-studio/lifecycleStatus'
import { getTenantEligibilityOverlay } from '../../../api/eligibilityOverlay'
import { ApiError } from '../../../api/problem'
import { listRecentOverlayIds } from '../../../lib/eligibilityOverlayRecents'
import { useOverlayCapabilities } from './useOverlayCapabilities'
import type { TenantEligibilityOverlayView } from '../../../types/eligibilityOverlay'

interface RecentRow {
  id: string
  view: TenantEligibilityOverlayView | null
  error: string | null
}

/**
 * `/admin/eligibility-overlays` (design doc §3.1/§3.2) — "Eligibility overlays (your tenant)", a
 * distinct `Admin` sub-section OUTSIDE `/admin/cartridge-studio/*` so a tenant user is never visually
 * led to believe they are editing canon (ADR-0056 §3.1, R-33).
 *
 * <p><b>Disclosed gap — "your recent overlays", not an authoritative tenant-wide list.</b> The
 * merged IB-288 controller exposes no `GET /tenant-eligibility-overlays?base_model_id=` list
 * endpoint — only `create`/`get`/`approve` by id. This screen therefore lists overlays the signed-in
 * user has created or opened IN THIS BROWSER (`lib/eligibilityOverlayRecents.ts`), each row
 * re-fetched LIVE via the real `GET .../{id}` (never fabricated data), plus an "Open by id" action
 * for any overlay id known out-of-band. It is labeled honestly as such, never presented as a
 * complete cross-device tenant list. Filed as an IB-288 follow-up (add the list endpoint).
 */
export function EligibilityOverlaysListScreen() {
  const navigate = useNavigate()
  const [rows, setRows] = useState<RecentRow[] | null>(null)
  const [openId, setOpenId] = useState('')
  const capabilities = useOverlayCapabilities()

  useEffect(() => {
    const controller = new AbortController()
    const ids = listRecentOverlayIds()
    if (ids.length === 0) {
      setRows([])
      return () => controller.abort()
    }
    setRows(null)
    Promise.all(
      ids.map((id) =>
        getTenantEligibilityOverlay(id, controller.signal)
          .then((view) => ({ id, view, error: null }) satisfies RecentRow)
          .catch((err) => ({
            id,
            view: null,
            error: err instanceof ApiError ? (err.problem.detail ?? err.message) : 'Could not load this overlay.',
          }) satisfies RecentRow),
      ),
    ).then((loaded) => {
      if (controller.signal.aborted) return
      setRows(loaded)
    })
    return () => controller.abort()
  }, [])

  return (
    <div style={{ padding: 'var(--iw-space-5)', display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-2)' }}>
        <Icon name="list-tree" size={18} />
        <h2 style={{ margin: 0, fontSize: 'var(--iw-size-md)' }}>Eligibility overlays (your tenant)</h2>
        <span style={{ flex: 1 }} />
        {/* AC1 — hidden, not disabled, without eligibility_overlay:X; AC7 fail-closed: absent
            while capability status is still 'loading' (no flash), and absent (never a default-
            permissive render) if the check itself errors, same as a resolved "no" (see
            `useOverlayCapabilities`). */}
        {capabilities.status === 'ready' && capabilities.canAuthor ? (
          <button type="button" className="iw-btn iw-btn--primary" onClick={() => navigate('/admin/eligibility-overlays/new')}>
            <Icon name="plus" size={14} /> New overlay
          </button>
        ) : null}
      </div>

      <InlineAlert severity="info">
        Canon stays platform-owned and read-only here — this section is only for supplementary
        content your tenant authors on top of it, gated maker ≠ checker.
      </InlineAlert>

      {capabilities.status === 'ready' && capabilities.error ? (
        <InlineAlert severity="warning">
          Couldn't confirm your access to author overlays ({capabilities.error}) — authoring is
          hidden until this can be confirmed.
        </InlineAlert>
      ) : null}

      <div style={{ display: 'flex', gap: 'var(--iw-space-2)', alignItems: 'flex-end' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-1)', flex: 1, maxWidth: 420 }}>
          <span style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>Open an overlay by id</span>
          <input className="iw-input" value={openId} onChange={(e) => setOpenId(e.target.value)} placeholder="overlay id" />
        </label>
        <button
          type="button"
          className="iw-btn"
          disabled={!openId}
          onClick={() => navigate(`/admin/eligibility-overlays/${openId}`)}
        >
          Open
        </button>
      </div>

      <div>
        <strong style={{ fontWeight: 500, fontSize: 'var(--iw-size-sm)' }}>Recently created/viewed in this browser</strong>
        <p style={{ margin: 'var(--iw-space-1) 0 var(--iw-space-3)', color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>
          Not a complete list of your tenant's overlays — only what this browser has created or opened.
        </p>

        {rows === null ? (
          <div style={{ color: 'var(--iw-text-muted)' }}>Loading…</div>
        ) : rows.length === 0 ? (
          <div style={{ color: 'var(--iw-text-muted)' }}>
            No overlays yet.{' '}
            {capabilities.status === 'ready' && capabilities.canAuthor
              ? <>Use <em>New overlay</em> above to author your tenant's first eligibility overlay.</>
              : 'You do not have permission to author one, or an overlay id can be opened directly below.'}
          </div>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-1)' }}>
            {rows.map((row) => (
              <li key={row.id}>
                {row.view ? (
                  <button
                    type="button"
                    className="iw-btn"
                    style={{ justifyContent: 'flex-start', gap: 'var(--iw-space-2)', width: '100%' }}
                    onClick={() => navigate(`/admin/eligibility-overlays/${row.id}`)}
                  >
                    <StatusPill {...lifecycleStatusPill(row.view.status)} />
                    <code>{row.id}</code>
                    <span style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>
                      base v{row.view.baseVersion} · overlay v{row.view.overlayVersion}
                    </span>
                  </button>
                ) : (
                  <InlineAlert severity="warning">
                    Overlay <code>{row.id}</code>: {row.error}
                  </InlineAlert>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
